<template>
	<!-- Loading State (only for initial loading) -->
	<div
		v-if="isLoading && properties.length === 0"
		data-testid="loading-state"
		class="size-full px-5 md:px-0"
	>
		<div class="grid min-h-full grid-cols-1 gap-4 py-5 md:grid-cols-3 md:py-8">
			<PropertyCardSkeleton
				v-for="i in 12"
				:key="i"
				variant="whiteBackground"
				size="sm"
			/>
		</div>
	</div>

	<!-- Empty State -->
	<div
		v-else-if="isEmpty && !isLoading"
		data-testid="empty-state"
		class="flex min-h-full flex-col items-center justify-center p-5 md:px-0 md:py-8"
	>
		<div class="flex w-full flex-col items-center justify-center gap-5">
			<div
				class="grid size-[76px] place-items-center rounded-full bg-white-dark-2"
			>
				<div
					class="grid size-[70%] place-items-center rounded-full bg-white-dark-4"
				>
					<IconMaterialSymbolsLightSearch class="size-7 text-white" />
				</div>
			</div>

			<div class="space-y-2 text-center">
				<p
					class="font-matter-sq text-xl font-normal leading-normal tracking-[-0.4px] text-primary"
				>
					{{ t('emptyState.title') }}
				</p>
				<p class="text-sm font-normal leading-normal text-primary md:text-base">
					{{ t('emptyState.message') }}
				</p>
			</div>

			<Button variant="secondary" as-child data-testid="explore-button">
				<NuxtLink :to="similarPropertiesHref" @click="handleExploreButtonClick">
					{{ t('emptyState.exploreButton') }}
					<IconMaterialSymbolsNorthEast class="w-4 md:w-5" />
				</NuxtLink>
			</Button>
		</div>
	</div>

	<!-- Properties List -->
	<div
		v-else-if="properties.length > 0"
		data-testid="properties-list"
		class="size-full px-5 md:px-0"
	>
		<div
			ref="internalScrollContainer"
			class="grid min-h-full grid-cols-1 gap-4 py-5 md:grid-cols-3 md:py-8"
		>
			<PropertyCard
				v-for="(property, index) in properties"
				:key="property.id"
				:property="property"
				:card-position="index"
				:disable-link="false"
				section="similar properties"
				variant="whiteBackground"
				size="sm"
				class="cursor-pointer"
				:tracking-context="trackingContext"
				:position-in-list="index"
			/>

			<!-- Loading Skeleton for next page -->
			<template v-if="isLoadingMore">
				<PropertyCardSkeleton
					v-for="i in itemsPerPage"
					:key="`loading-${i}`"
					variant="whiteBackground"
					size="sm"
				/>
			</template>
		</div>
	</div>
</template>

<script setup lang="ts">
import type { ListingTrackingContext } from '~/composables/useListingEventTracking';
import type { Property } from '~/core/entities/property';

import { useInfiniteScroll } from '@vueuse/core';
import { useEvents } from '~/composables/events/useEvents';
import { buildCarouselTrackingContext } from '~/composables/useListingEventTracking';
import { MixpanelProps } from '~/core/service/mixpanel';
import { PropertyFiltersPresenter } from '~/presenters/PropertyFiltersPresenter';
import { useI18n } from 'vue-i18n';

import IconMaterialSymbolsNorthEast from '~icons/material-symbols-light/north-east';
import IconMaterialSymbolsLightSearch from '~icons/material-symbols-light/search';

import { Button } from '../ui/button';
import PropertyCard from './Card.vue';
import PropertyCardSkeleton from './PropertyCardSkeleton.vue';
import messages from './SimilarPropertiesList.locale.json';

interface SimilarPropertiesListProps {
	properties: Property[];
	isLoading: boolean;
	hasMore: boolean;
	isEmpty: boolean;
	baseProperty: Property;
	scrollContainer?: HTMLElement | null; // External scroll container for modal-level scrolling
}

interface SimilarPropertiesListEmits {
	'load-more': [];
}

const itemsPerPage = 12;

const props = defineProps<SimilarPropertiesListProps>();
const emit = defineEmits<SimilarPropertiesListEmits>();

const { t } = useI18n({
	useScope: 'local',
	messages
});

const route = useRoute();
const { trackCtaViewMoreClicked } = useEvents();

// Refs
const internalScrollContainer = ref<HTMLElement | null>(null);
const isLoadingMore = ref(false);

const trackingContext = computed<ListingTrackingContext>(() =>
	buildCarouselTrackingContext({
		placement: 'carrossel_similares',
		properties: props.properties,
		originProperty: props.baseProperty?.id ?? null
	})
);

// Use external scroll container if provided (modal-level), otherwise use internal
const activeScrollContainer = computed(
	() => props.scrollContainer || internalScrollContainer.value
);

const similarPropertiesHref = computed(() => {
	if (!props.baseProperty) return '/venda/imoveis';

	return PropertyFiltersPresenter.getPathFromFilters({
		regions: props.baseProperty.region ? [props.baseProperty.region] : [],
		cities: props.baseProperty.city ? [props.baseProperty.city] : [],
		states: props.baseProperty.state ? [props.baseProperty.state] : [],
		propertyTypes: [props.baseProperty.propertyType?.identifier].filter(Boolean)
	});
});

function handleExploreButtonClick(): void {
	const destinationUrl = similarPropertiesHref.value;

	trackCtaViewMoreClicked({
		[MixpanelProps.CURRENT_PAGE]: route.fullPath,
		[MixpanelProps.SECTION_NAME]: 'similar properties',
		[MixpanelProps.DESTINATION_URL]: destinationUrl,
		[MixpanelProps.CTA_TEXT]: t('emptyState.exploreButton')
	});
}

async function handleLoadMore(): Promise<void> {
	if (!props.hasMore || isLoadingMore.value) return;

	isLoadingMore.value = true;
	emit('load-more');
}

// Infinite scroll - works with both internal and external containers
useInfiniteScroll(activeScrollContainer, handleLoadMore, {
	distance: 200, // 200px from bottom
	canLoadMore: () => props.hasMore && !isLoadingMore.value
});

// Watchers
watch(
	() => props.properties.length,
	(newLength: number, oldLength: number) => {
		if (newLength > oldLength && isLoadingMore.value) {
			nextTick(() => {
				isLoadingMore.value = false;
			});
		}
	}
);
</script>
